/**
 * Azure Entra Account Utilities
 * 
 * Reusable functions for extracting user information from Azure Entra ID / MSAL accounts
 * Handles both Azure AD and Azure Entra External ID (CIAM/B2C) token claims
 * 
 * This utility ensures consistent email extraction across all authentication flows,
 * preventing GUID emails from being displayed instead of actual email addresses.
 */

import { AccountInfo, AuthenticationResult } from '@azure/msal-browser';
import type { User } from '../context/AuthContext';

/**
 * Check if a string looks like an email address
 * Used to filter out GUIDs and other non-email identifiers
 */
export function isEmailLike(str: string | undefined | null): boolean {
  if (!str) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str);
}

/**
 * Check if a string is an Entra Object ID (GUID/UUID format).
 * Used to ensure claim and ownership flows have proper user context from Entra.
 */
export function isEntraObjectId(id: string | undefined | null): boolean {
  if (!id || typeof id !== 'string') return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id.trim());
}

/**
 * Extract email address from decoded token claims
 * 
 * Shared function for extracting email from claims (used by both MSAL and OAuth2)
 * Includes email validation to prevent GUID emails.
 * 
 * Priority order:
 * 1. claims.email
 * 2. claims.emails[0] (array format for External ID)
 * 3. claims.preferred_username (only if email-like)
 * 4. claims.signInNames.emailAddress (External ID specific)
 * 
 * @param claims - Decoded token claims object
 * @param username - Optional username from account (for MSAL fallback)
 * @returns Email address or empty string
 */
export function extractEmailFromClaims(
  claims: Record<string, any>,
  username?: string | null
): string {
  return (
    (claims.email as string) ??
    (claims.emails as string[])?.[0] ??
    (claims.preferred_username && isEmailLike(claims.preferred_username as string)
      ? claims.preferred_username as string
      : undefined) ??
    ((claims.signInNames as any)?.emailAddress as string) ?? // External ID claim
    (username && isEmailLike(username) ? username : undefined) ?? // Only use username if it's email-like
    ''
  );
}

/**
 * Extract user name from decoded token claims
 * 
 * Shared function for extracting name from claims (used by both MSAL and OAuth2)
 * 
 * Priority order:
 * 1. claims.name
 * 2. claims.display_name (External ID)
 * 3. given_name + surname (combined)
 * 4. given_name
 * 5. surname
 * 6. email username (fallback)
 * 
 * @param claims - Decoded token claims object
 * @param accountName - Optional account name (for MSAL)
 * @param email - Email address (used as fallback for name)
 * @returns User name or email username
 */
export function extractNameFromClaims(
  claims: Record<string, any>,
  accountName?: string | null,
  email?: string
): string {
  const fallbackName = email?.split('@')[0] ?? '';

  return (
    accountName ??
    (claims.name as string) ??
    (claims.display_name as string) ?? // External ID claim
    (claims.displayName as string) ?? // Alternative External ID claim
    ((claims.given_name && claims.surname)
      ? `${claims.given_name} ${claims.surname}`
      : (claims.given_name as string)) ??
    (claims.surname as string) ??
    fallbackName
  );
}

/**
 * Extract user ID from decoded token claims
 * 
 * Shared function for extracting ID from claims (used by both MSAL and OAuth2)
 * 
 * Priority order:
 * 1. claims.oid (Object ID - preferred for External ID)
 * 2. claims.sub (Subject - External ID)
 * 3. claims.user_id
 * 4. claims.objectId
 * 5. email (fallback)
 * 
 * @param claims - Decoded token claims object
 * @param localAccountId - Optional local account ID (for MSAL)
 * @param homeAccountId - Optional home account ID (for MSAL)
 * @param username - Optional username (for MSAL fallback)
 * @param email - Optional email (for fallback)
 * @returns User ID or empty string
 */
export function extractIdFromClaims(
  claims: Record<string, any>,
  localAccountId?: string | null,
  homeAccountId?: string | null,
  username?: string | null,
  email?: string
): string {
  return (
    localAccountId ??
    homeAccountId ??
    (claims.oid as string) ?? // Object ID (preferred for External ID)
    (claims.sub as string) ?? // Subject (External ID)
    (claims.user_id as string) ??
    (claims.objectId as string) ??
    username ??
    email ??
    ''
  );
}

/**
 * Extract user roles from decoded token claims
 * 
 * Shared function for extracting roles from claims (used by both MSAL and OAuth2)
 * 
 * Priority order:
 * 1. claims.roles
 * 2. claims.groups
 * 3. claims.extension_Roles (custom claim for External ID)
 * 
 * @param claims - Decoded token claims object
 * @returns Array of role strings
 */
export function extractRolesFromClaims(claims: Record<string, any>): string[] {
  return (
    (claims.roles as string[]) ??
    (claims.groups as string[]) ??
    (claims['extension_Roles'] as string[]) ?? // Custom claim for External ID
    (claims.extension_Roles as string[]) ?? // Alternative format
    []
  );
}

/**
 * Extract complete user information from decoded token claims
 * 
 * Shared function for extracting user data from decoded claims.
 * Used by both MSAL (via AccountInfo) and OAuth2 (via decoded JWT).
 * Includes email validation to prevent GUID emails.
 * 
 * @param claims - Decoded token claims object
 * @param options - Optional account-specific fields (for MSAL)
 * @returns User object with id, email, name, and roles
 */
export function extractUserFromClaims(
  claims: Record<string, any>,
  options?: {
    accountName?: string | null;
    localAccountId?: string | null;
    homeAccountId?: string | null;
    username?: string | null;
  }
): User {
  // Extract email first (needed for name fallback)
  const email = extractEmailFromClaims(claims, options?.username);

  // Extract other user information
  const name = extractNameFromClaims(claims, options?.accountName, email);
  const id = extractIdFromClaims(
    claims,
    options?.localAccountId,
    options?.homeAccountId,
    options?.username,
    email
  );
  const roles = extractRolesFromClaims(claims);

  return {
    id,
    email,
    name,
    roles,
  };
}

/**
 * Extract email address from Azure Entra token claims
 * 
 * Priority order:
 * 1. responseClaims.email (fresh token claims from AuthenticationResult)
 * 2. responseClaims.emails[0] (array format for External ID)
 * 3. responseClaims.preferred_username (only if email-like)
 * 4. accountClaims.email (cached claims from AccountInfo)
 * 5. accountClaims.emails[0] (array format)
 * 6. accountClaims.preferred_username (only if email-like)
 * 7. accountClaims.signInNames.emailAddress (External ID specific)
 * 8. account.username (only if email-like - may be GUID in External ID)
 * 
 * @param account - MSAL AccountInfo object
 * @param responseClaims - Optional fresh token claims from AuthenticationResult.idTokenClaims
 * @returns Email address or empty string
 */
export function extractEmailFromAccount(
  account: AccountInfo | null,
  responseClaims?: Record<string, any> | null
): string {
  if (!account) {
    return '';
  }

  const accountClaims = account.idTokenClaims ?? {} as Record<string, any>;

  // Priority: responseClaims (fresh) > accountClaims (cached) > account.username
  // Try responseClaims first, then fall back to accountClaims
  if (responseClaims) {
    const email = extractEmailFromClaims(responseClaims, account.username);
    if (email) return email;
  }
  
  // Fall back to accountClaims if responseClaims didn't yield an email
  return extractEmailFromClaims(accountClaims, account.username);
}

/**
 * Extract user name from Azure Entra token claims
 * 
 * Priority order:
 * 1. account.name
 * 2. responseClaims.name (fresh)
 * 3. accountClaims.name (cached)
 * 4. given_name + surname (combined)
 * 5. given_name
 * 6. displayName (External ID)
 * 7. email username (fallback)
 * 
 * @param account - MSAL AccountInfo object
 * @param responseClaims - Optional fresh token claims
 * @param email - Email address (used as fallback for name)
 * @returns User name or email username
 */
export function extractNameFromAccount(
  account: AccountInfo | null,
  responseClaims?: Record<string, any> | null,
  email?: string
): string {
  if (!account) {
    return email?.split('@')[0] ?? '';
  }

  const accountClaims = account.idTokenClaims ?? {} as Record<string, any>;
  const primaryClaims = responseClaims ?? accountClaims;

  // Use shared function for consistency
  return extractNameFromClaims(primaryClaims, account.name, email);
}

/**
 * Extract user ID from Azure Entra token claims
 * 
 * Priority order:
 * 1. account.localAccountId
 * 2. account.homeAccountId
 * 3. responseClaims.oid (Object ID - preferred for External ID)
 * 4. accountClaims.oid
 * 5. responseClaims.sub (Subject - External ID)
 * 6. accountClaims.sub
 * 7. account.username (fallback)
 * 
 * @param account - MSAL AccountInfo object
 * @param responseClaims - Optional fresh token claims
 * @returns User ID or empty string
 */
export function extractIdFromAccount(
  account: AccountInfo | null,
  responseClaims?: Record<string, any> | null
): string {
  if (!account) {
    return '';
  }

  const accountClaims = account.idTokenClaims ?? {} as Record<string, any>;
  const primaryClaims = responseClaims ?? accountClaims;

  // Use shared function for consistency
  return extractIdFromClaims(
    primaryClaims,
    account.localAccountId,
    account.homeAccountId,
    account.username
  );
}

/**
 * Extract user roles from Azure Entra token claims
 * 
 * Priority order:
 * 1. responseClaims.roles (fresh)
 * 2. accountClaims.roles
 * 3. responseClaims.groups (fresh)
 * 4. accountClaims.groups
 * 5. accountClaims.extension_Roles (custom claim for External ID)
 * 
 * @param account - MSAL AccountInfo object
 * @param responseClaims - Optional fresh token claims
 * @returns Array of role strings
 */
export function extractRolesFromAccount(
  account: AccountInfo | null,
  responseClaims?: Record<string, any> | null
): string[] {
  if (!account) {
    return [];
  }

  const accountClaims = account.idTokenClaims ?? {} as Record<string, any>;
  const primaryClaims = responseClaims ?? accountClaims;

  // Use shared function for consistency
  return extractRolesFromClaims(primaryClaims);
}

/**
 * Extract complete user information from Azure Entra MSAL account
 * 
 * This is the main function to use for extracting user data from MSAL accounts.
 * It handles both Azure AD and Azure Entra External ID token structures.
 * 
 * @param account - MSAL AccountInfo object (required)
 * @param responseClaims - Optional fresh token claims from AuthenticationResult.idTokenClaims
 *                        If provided, these take priority over account.idTokenClaims
 * @returns User object with id, email, name, and roles
 * @throws Error if account is null
 */
export function extractUserFromAccount(
  account: AccountInfo | null,
  responseClaims?: Record<string, any> | null
): User {
  if (!account) {
    throw new Error('No account information available');
  }

  // Extract email first (needed for name fallback)
  const email = extractEmailFromAccount(account, responseClaims);
  
  // Extract other user information
  const name = extractNameFromAccount(account, responseClaims, email);
  const id = extractIdFromAccount(account, responseClaims);
  const roles = extractRolesFromAccount(account, responseClaims);

  return {
    id,
    email,
    name,
    roles,
  };
}

/**
 * Extract user information from MSAL AuthenticationResult
 * 
 * Convenience function that extracts user info from an AuthenticationResult,
 * prioritizing response.idTokenClaims over account.idTokenClaims.
 * 
 * @param response - MSAL AuthenticationResult
 * @returns User object with id, email, name, and roles
 * @throws Error if response.account is null
 */
export function extractUserFromAuthenticationResult(response: AuthenticationResult): User {
  if (!response.account) {
    throw new Error('No account information in authentication result');
  }

  const responseClaims = response.idTokenClaims as Record<string, any> | undefined;
  return extractUserFromAccount(response.account, responseClaims);
}

/**
 * Validate if a stored user email is valid (not a GUID)
 * 
 * Used to detect and fix old stored user data that may contain GUID emails
 * from before the email extraction fix was applied.
 * 
 * @param user - User object to validate
 * @returns true if email is valid (email-like), false if it's a GUID or invalid
 */
export function isUserEmailValid(user: User | null): boolean {
  if (!user || !user.email) {
    return false;
  }
  return isEmailLike(user.email);
}
